import React, { useContext, useEffect, useRef, useState } from "react";
import More from "../../../../components/SVGComponents/Tools/More";
import {
  DROPDOWNTOOLS,
  TOOLS,
  MPR_TOOLS,
  MPR_MORE_TOOLS,
  STACK_SCROLL_DROPDOWN_ELEMENTS,
  WINDOW_LEVEL_PRESETS,
  WINDOW_LEVEL_MODALITY_LIST,
  SYNC_MODES,
  MEASURE_TOOLS,
} from "../../../../constants/Constants";
import ImageEnhancementTool from "../Tools/ToolComponents/ImageEnhancementTool";
import ImageEnhancementIcon from "../../../../components/SVGComponents/Tools/ImageEnhancementIcon";
import DropDownToolElement from "./DropDownToolElement";
import MPRTool from "../Tools/ToolComponents/MPRTool";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import { ArrowAnnotateTool, ProbeTool, StackScrollTool, WindowLevelTool, ZoomTool } from "@cornerstonejs/tools";
import {
  disableStackScrollSync,
  enableStackScrollSync,
} from "../../helpers/StackScrollSyncService";
import { isMobile } from "react-device-detect";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import DeleteIcon from "../../../../components/SVGComponents/DeleteIcon";
import themeContext from "../../../../context/theme/ThemeContext";
import WindowLevelPresets from "../Tools/ToolComponents/WindowLevelPresets";
import {
  fitToScreen,
  originalResolution,
  resizeViewport,
  resetMPRViewports,
} from "../../helpers/CornerstoneViewportService";
import { VOLUME_3D_VIEWPORT_INDEX } from "../../helpers/MPRViewportInput";
import { useTranslation } from "react-i18next";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import HPIcon from "../../../../components/SVGComponents/Tools/HPIcon";
import ToolbarMoreMenuRow from "./ToolbarMoreMenuRow";
import { MOUSE_ASSIGNABLE_TOOL_NAMES } from "../../../../constants/MouseButtonToolAssignments";

const ToolDropDown = () => {
  const { t } = useTranslation();
  const [showDropDown, setShowDropDown] = useState(false);
  const [expandedPortraitTool, setExpandedPortraitTool] = useState(null);
  const {
    studyModality,
    isLandscape,
    stackSize,
    hpDataList,
    hpPercentageMap,
    appliedHp,
    setAppliedHp,
    isHPApplied,
  } = useContext(studyviewerContext);
  const {
    activeModality,
    activeThumbnailIndex,
    setStackScrollActiveThumbnailIndex,
  } = useContext(ThumbnailContext);
  const { showKeyImagesAndTextAnnotation } = useContext(studyReportContext);
  const {
    isChildStudyActive,
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    isMPRActive,
    activeViewportIndex,
    activeMPRViewportIndex,
    activeMPRTool,
    setActiveMPRTool,
    setPreviousActiveMPRTool,
    boundingBoxMode,
    setBoundingBoxMode,
    isCropEnable,
    setIsCropEnable,
    handleDefaultToolChange,
    isMPRCineEnabled,
    setIsMPRCineEnabled,
    isSynchronizedEnabled,
    setIsSynchronizedEnabled,
    syncMode,
    setSyncMode,
    viewportSeriesData,
    linkedViewports,
    setLinkedViewports,
    removeSelectedMeasurement,
    removeAllMeasurements,
    assignMouseButtonToAssignableTool,
    isSpineLabellingActive,
    setIsSpineLabellingActive,
    spineLabels,
    setSpineLabels,
    setSpinePhase,
    exitHangingProtocol,
    reapplyHangingProtocol,
  } = useContext(ViewportContext);
  const { theme } = useContext(themeContext);
  // Measurements are drawn on the MPR panes only — with the 3D pane active
  // (same condition MPRTool uses to grey out annotation/measurement tools),
  // there's nothing on it to delete.
  const isMeasurementDeletionDisabled =
    isMPRActive && activeMPRViewportIndex === VOLUME_3D_VIEWPORT_INDEX;
  const [hoverDeleteSelected, setHoverDeleteSelected] = useState(false);
  const [hoverDeleteAll, setHoverDeleteAll] = useState(false);
  const [flyoutTopOffset, setFlyoutTopOffset] = useState(0);
  const panelsWrapperRef = useRef(null);
  const flyoutTimerRef = useRef(null);
  // Must be a ref, not state — clearTimeout() inside closures needs the live
  // timer ID. State updates are async so reading delayHandler from a useState
  // closure gives a stale value and the cancel never lands.
  const delayHandlerRef = useRef(null);

  const handleDropDownClick = () => {
    setShowDropDown((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelsWrapperRef.current && !panelsWrapperRef.current.contains(event.target)) {
        setShowDropDown(false);
        setExpandedPortraitTool(null);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const hideContextMenu = () => {
    clearTimeout(delayHandlerRef.current);
    delayHandlerRef.current = setTimeout(() => {
      setShowDropDown(false);
      setExpandedPortraitTool(null);
    }, 300);
  };

  const onShowContextualMenu = (ev) => {
    ev.preventDefault();
    clearTimeout(delayHandlerRef.current);
    delayHandlerRef.current = setTimeout(() => {
      setShowDropDown(true);
    }, 300);
  };

  // Flyout hover helpers
  const showFlyout = (toolKey, rowElement) => {
    clearTimeout(flyoutTimerRef.current);
    clearTimeout(delayHandlerRef.current);
    setExpandedPortraitTool(toolKey);
    if (rowElement && panelsWrapperRef.current) {
      const rowRect = rowElement.getBoundingClientRect();
      const wrapperRect = panelsWrapperRef.current.getBoundingClientRect();
      setFlyoutTopOffset(rowRect.top - wrapperRect.top);
    }
  };

  const hideFlyout = () => {
    flyoutTimerRef.current = setTimeout(() => {
      setExpandedPortraitTool(null);
    }, 200);
  };

  const cancelHideFlyout = () => {
    clearTimeout(flyoutTimerRef.current);
    clearTimeout(delayHandlerRef.current);
  };

  // Portrait tool activation — handles both regular and MPR modes
  const handlePortraitToolActivate = (toolKey) => {
    setBoundingBoxMode(false);
    setIsCropEnable(false);
    if (toolKey === "CINE" && isMPRActive) {
      setIsMPRCineEnabled(!isMPRCineEnabled);
      setShowDropDown(false);
      setExpandedPortraitTool(null);
      return;
    }
    if (toolKey === "Reset3D") {
      // One-shot action on the 3D volume-render viewport, not a primary MPR
      // tool — doesn't touch activeMPRTool.
      resetMPRViewports();
      setShowDropDown(false);
      setExpandedPortraitTool(null);
      return;
    }
    if (isMPRActive) {
      if (activeMPRTool === toolKey) {
        setPreviousActiveMPRTool(activeMPRTool);
        handleDefaultToolChange();
      } else {
        setPreviousActiveMPRTool(activeMPRTool);
        if (MOUSE_ASSIGNABLE_TOOL_NAMES.includes(toolKey)) {
          assignMouseButtonToAssignableTool("primary", toolKey, true);
        }
        setActiveMPRTool(toolKey);
      }
    } else {
      if (activeTool === toolKey) {
        handleDefaultToolChange();
      } else {
        setPreviousActiveTool(activeTool);
        setActiveTool(toolKey);
      }
    }
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  // Sub-item action handlers
  const handleSeriesChange = (key) => {
    if (key === STACK_SCROLL_DROPDOWN_ELEMENTS[0].key) {
      if (stackSize === 1 || activeThumbnailIndex === 0) return;
      setStackScrollActiveThumbnailIndex(activeThumbnailIndex - 1);
    } else if (key === STACK_SCROLL_DROPDOWN_ELEMENTS[1].key) {
      if (activeThumbnailIndex === stackSize - 1) return;
      setStackScrollActiveThumbnailIndex(activeThumbnailIndex + 1);
    }
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  const handleFitToScreen = async () => {
    await fitToScreen(isMPRActive ? activeMPRViewportIndex : activeViewportIndex, isMPRActive);
    resizeViewport();
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  const handleOriginalResolution = async () => {
    await originalResolution(isMPRActive ? activeMPRViewportIndex : activeViewportIndex, isMPRActive);
    resizeViewport();
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  const handleHPClick = (hpName) => {
    const hp = Array.isArray(hpDataList) ? hpDataList.find((h) => h.name === hpName) : null;
    if (hp) setAppliedHp(hp);
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  // Returns whether a portrait tool has flyout sub-items for the current modality
  const toolHasSubItems = (toolKey) => {
    if (toolKey === WindowLevelTool.toolName)
      return WINDOW_LEVEL_MODALITY_LIST.includes(activeModality);
    return (
      toolKey === StackScrollTool.toolName || toolKey === ZoomTool.toolName || toolKey === "Synchronize" || toolKey === "SpineLabelling"
    );
  };

  // Same toggle as the toolbar HP icon: applied -> back to the default 1x1 layout,
  // not applied -> re-hang the last (or best matching) protocol.
  const handleHPToggle = () => {
    if (isHPApplied) exitHangingProtocol();
    else reapplyHangingProtocol();
    setShowDropDown(false);
    setExpandedPortraitTool(null);
  };

  const hpHasProtocols = () =>
    hpPercentageMap &&
    hpPercentageMap.size > 0 &&
    Array.from(hpPercentageMap).some(([, v]) => v !== 0);

  // Content rendered inside the separate flyout panel
  const renderFlyoutContent = (toolKey) => {
    if (toolKey === StackScrollTool.toolName) {
      return STACK_SCROLL_DROPDOWN_ELEMENTS.map((ss, idx) => (
        <div
          key={idx}
          onClick={() => handleSeriesChange(ss.key)}
          className={`stack-scroll-dropdown-elements ${
            (idx === 0 && activeThumbnailIndex === 0) ||
            (idx === 1 && activeThumbnailIndex === stackSize - 1)
              ? "stack-scroll-dropdown-elements-disabled"
              : ""
          }`}
        >
          {ss.key}
        </div>
      ));
    }

    if (toolKey === WindowLevelTool.toolName) {
      if (!WINDOW_LEVEL_MODALITY_LIST.includes(activeModality)) return null;
      return WINDOW_LEVEL_PRESETS.map((wl, idx) => (
        <WindowLevelPresets key={idx} wl={wl} setShowDropDown={setShowDropDown} />
      ));
    }

    if (toolKey === ZoomTool.toolName) {
      return (
        <>
          <div onClick={handleFitToScreen} className="stack-scroll-dropdown-elements">
            {t("viewer.tools.fit-to-screen")}
          </div>
          <div onClick={handleOriginalResolution} className="stack-scroll-dropdown-elements">
            {t("viewer.tools.original-zoom")}
          </div>
        </>
      );
    }

    if (toolKey === "Synchronize") {
      const syncModeItems = [
        { mode: SYNC_MODES.DISABLED, labelKey: "viewer.tools.disabled-sync", fallback: "Disabled" },
        { mode: SYNC_MODES.MANUAL, labelKey: "viewer.tools.manual-sync", fallback: "Manual" },
        { mode: SYNC_MODES.AUTOMATIC, labelKey: "viewer.tools.auto-sync", fallback: "Automatic" },
        { mode: SYNC_MODES.DISTANCE, labelKey: "viewer.tools.distance-sync", fallback: "Distance" },
      ];

      const handleSyncModeChange = (mode) => {
        if (mode === SYNC_MODES.DISABLED) {
          disableStackScrollSync();
          setIsSynchronizedEnabled(false);
          setSyncMode(SYNC_MODES.DISABLED);
        } else {
          const shouldClearManualLinks = mode === SYNC_MODES.AUTOMATIC && linkedViewports.length > 0;
          if (shouldClearManualLinks) {
            setLinkedViewports([]);
          }
          setSyncMode(mode);
          disableStackScrollSync();
          enableStackScrollSync(
            activeViewportIndex,
            mode,
            viewportSeriesData,
            shouldClearManualLinks ? null : (linkedViewports.length > 0 ? linkedViewports : null)
          );
          setIsSynchronizedEnabled(true);
        }
        setShowDropDown(false);
        setExpandedPortraitTool(null);
      };

      return syncModeItems.map(({ mode, labelKey, fallback }) => {
        const isSelected = mode === SYNC_MODES.DISABLED
          ? !isSynchronizedEnabled
          : isSynchronizedEnabled && syncMode === mode;
        return (
          <div
            key={mode}
            className="stack-scroll-dropdown-elements"
            onClick={() => handleSyncModeChange(mode)}
            style={{
              justifyContent: "space-between",
              gap: "10px",
              cursor: "pointer",
              padding: "0 12px",
            }}
          >
            <span>{t(labelKey, fallback)}</span>
            <span
              style={{
                display: "inline-block",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: isSelected ? "#0078d4" : "#585858",
                border: isSelected ? "none" : "1px solid #888",
                flexShrink: 0,
              }}
            />
          </div>
        );
      });
    }

    if (toolKey === "SpineLabelling") {
      const handleSpineAdd = () => {
        setIsSpineLabellingActive(true);
        setShowDropDown(false);
        setExpandedPortraitTool(null);
      };
      const handleSpineReset = () => {
        setSpineLabels([]);
        setSpinePhase("select");
        setIsSpineLabellingActive(false);
        setShowDropDown(false);
        setExpandedPortraitTool(null);
      };
      return (
        <>
          <div
            className="stack-scroll-dropdown-elements"
            onClick={handleSpineAdd}
            style={{ cursor: "pointer", padding: "0 12px" }}
          >
            <span>Add</span>
          </div>
          <div
            className="stack-scroll-dropdown-elements"
            onClick={handleSpineReset}
            style={{ cursor: "pointer", padding: "0 12px" }}
          >
            <span>Reset</span>
          </div>
        </>
      );
    }

    if (toolKey === "HP") {
      if (!hpHasProtocols()) return null;
      return Array.from(hpPercentageMap)
        .filter(([, v]) => v !== 0)
        .map(([key]) => (
          <div
            key={key}
            onClick={() => handleHPClick(key)}
            style={{ color: key === appliedHp?.name ? "#0085FF" : "inherit" }}
            className="stack-scroll-dropdown-elements"
          >
            {key}
          </div>
        ));
    }

    return null;
  };

  // Portrait tool rows inside the main dropdown (portrait mode only, works in both normal and MPR modes)
  const renderPortraitTools = () => {
    if (isLandscape) return null;
    // Non-mobile portrait: all tools (regular and MPR) are in the toolbar row
    if (!isMobile) return null;

    const toolsToRender = isMPRActive
      ? MPR_TOOLS.filter((tool) => !(tool.NotFor?.includes(activeModality)))
      : TOOLS.filter((tool) => !tool.NotFor.includes(activeModality));

    const activeToolKey = isMPRActive ? activeMPRTool : activeTool;

    return (
      <>
        {toolsToRender.map((tool) => {
          const hasSubItems = toolHasSubItems(tool.key);

          if (!hasSubItems) {
            return isMPRActive ? (
              // MPR simple tools: render inline since DropDownToolElement uses regular activeTool
              <div
                key={`portrait_${tool.key}`}
                style={{
                  height: "40px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => handlePortraitToolActivate(tool.key)}
              >
                <div className="highlight-tool">
                  <GetToolSVG toolName={tool.key} hovered={activeToolKey === tool.key} />
                </div>
                <span style={{ marginLeft: "10px" }}>{t(tool.name)}</span>
              </div>
            ) : (
              <div key={`portrait_${tool.key}`}>
                <DropDownToolElement tool={tool} setShowDropDown={setShowDropDown} />
              </div>
            );
          }

          return (
            <div
              key={`portrait_${tool.key}`}
              onMouseEnter={(e) => showFlyout(tool.key, e.currentTarget)}
              onMouseLeave={hideFlyout}
            >
              <div
                style={{
                  height: "40px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{ flex: 1, display: "flex", alignItems: "center" }}
                  onClick={() => handlePortraitToolActivate(tool.key)}
                >
                  <div className="highlight-tool">
                    <GetToolSVG toolName={tool.key} hovered={activeToolKey === tool.key} />
                  </div>
                  <span style={{ marginLeft: "10px" }}>{t(tool.name)}</span>
                </div>
                <span style={{ fontSize: "10px", marginLeft: "8px", opacity: 0.6 }}>▶</span>
              </div>
            </div>
          );
        })}

        {/* HP Tool row — only in non-MPR portrait mode */}
        {!isMPRActive && (
          <div
            onMouseEnter={(e) => hpHasProtocols() && showFlyout("HP", e.currentTarget)}
            onMouseLeave={hideFlyout}
          >
            <div
              style={{
                height: "40px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{ flex: 1, display: "flex", alignItems: "center" }}
                onClick={handleHPToggle}
              >
                <div className="highlight-tool">
                  <HPIcon
                    color={isHPApplied ? theme.palette.activeTool : theme.palette.inactiveTool}
                  />
                </div>
                <span style={{ marginLeft: "10px" }}>
                  {isHPApplied
                    ? t("viewer.tools.exit-hanging-protocol")
                    : t("viewer.tools.hanging-protocol")}
                </span>
              </div>
              {hpHasProtocols() && (
                <span style={{ fontSize: "10px", marginLeft: "8px", opacity: 0.6 }}>▶</span>
              )}
            </div>
          </div>
        )}

      </>
    );
  };

  const flyoutContent =
    expandedPortraitTool && (!isLandscape || expandedPortraitTool === "Synchronize" || expandedPortraitTool === "SpineLabelling")
      ? renderFlyoutContent(expandedPortraitTool)
      : null;

  const dropdownPanelStyle = {
    display: "flex",
    flexDirection: "column",
    minWidth: "180px",
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow:
      "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
    borderRadius: "5px",
    paddingLeft: "0",
    paddingRight: "0",
  };

  const flyoutPanelStyle = {
    display: "flex",
    flexDirection: "column",
    minWidth: "160px",
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
    marginLeft: "4px",
    marginTop: `${flyoutTopOffset}px`,
    boxShadow:
      "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
    borderRadius: "5px",
    paddingLeft: "0",
    paddingRight: "0",
    paddingTop: "6px",
    paddingBottom: "6px",
  };

  return (
    <>
      <ImageEnhancementTool hideTrigger />
      <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
        {/* More button trigger */}
        <div
          className="highlight-tool"
          onClick={handleDropDownClick}
          onMouseEnter={onShowContextualMenu}
          onMouseLeave={hideContextMenu}
        >
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <More />
          </div>
        </div>

        {/* Panels wrapper: flex row so flyout panel sits to the right of the main dropdown */}
        <div
          ref={panelsWrapperRef}
          onMouseEnter={onShowContextualMenu}
          onMouseLeave={hideContextMenu}
          style={{
            display: showDropDown ? "flex" : "none",
            //flexDirection: (!isLandscape && !isMobile) ? "row-reverse" : "row",
            flexDirection: "row",
            position: "absolute",
            top: "100%",
            zIndex: 9999,
            left: 0,
          }}
        >
          {/* Main dropdown panel */}
          <div
            className="viewer-tool-more-dropdown viewer-toolbar-more-menu viewer-tool-more-dropdown-height react-scroll"
            style={dropdownPanelStyle}
          >
            {renderPortraitTools()}
            {isMPRActive &&
              MPR_MORE_TOOLS.filter(
                (tool) => !(tool.NotFor?.includes(activeModality))
              ).map((tool, index) => (
                <div key={`mpr-more-${index}`}>
                  <MPRTool
                    tool={tool}
                    variant="menu"
                    onSelect={() => setShowDropDown(false)}
                  />
                </div>
              ))}
            {!isMPRActive && DROPDOWNTOOLS.filter((tool, index) => {
              if (MEASURE_TOOLS.some((m) => m.key === tool.key)) return false;
              if (isLandscape) return true;
              // Non-mobile portrait: only show tools from Probe onwards that aren't already in the main TOOLS toolbar
              if (!isMobile) {
                const probeIndex = DROPDOWNTOOLS.findIndex(t => t.key === ProbeTool.toolName);
                return index >= probeIndex && !TOOLS.some((t) => t.key === tool.key);
              }
              return !TOOLS.some((t) => t.key === tool.key);
            }).map((tool, index) => {
              if (!tool.NotFor.includes(activeModality)) {
                if (tool.key === "SpineLabelling") {
                  return (
                    <div
                      key={`${tool}_${index}`}
                      onMouseEnter={(e) => showFlyout("SpineLabelling", e.currentTarget)}
                      onMouseLeave={hideFlyout}
                    >
                      <DropDownToolElement tool={tool} setShowDropDown={setShowDropDown} hideSpineSubmenu />
                    </div>
                  );
                }
                if (tool.key === "Synchronize") {
                  return (
                    <div
                      key={`${tool}_${index}`}
                      onMouseEnter={(e) => showFlyout("Synchronize", e.currentTarget)}
                      onMouseLeave={hideFlyout}
                    >
                      <DropDownToolElement tool={tool} setShowDropDown={setShowDropDown} hideSyncSubmenu />
                    </div>
                  );
                }
                if (tool.key === ArrowAnnotateTool.toolName) {
                  if (showKeyImagesAndTextAnnotation && !isChildStudyActive) {
                    return (
                      <div key={`${tool}_${index}`}>
                        <DropDownToolElement tool={tool} setShowDropDown={setShowDropDown} />
                      </div>
                    );
                  }
                } else {
                  return (
                    <div key={`${tool}_${index}`}>
                      <DropDownToolElement tool={tool} setShowDropDown={setShowDropDown} />
                    </div>
                  );
                }
              }
            })}
            <ToolbarMoreMenuRow
              label={t("viewer.tools.image-enhancement", "Image Enhancement")}
              onClick={() => {
                document.dispatchEvent(new CustomEvent("toggleImageEnhancement"));
                setShowDropDown(false);
              }}
            >
              <div className="highlight-tool">
                <ImageEnhancementIcon color={theme.palette.inactiveTool} />
              </div>
            </ToolbarMoreMenuRow>
            {/* Delete Selected / Delete All now live in the Measure dropdown for the
                non-MPR desktop toolbar. Keep them in this "..." menu ONLY for MPR mode
                (where the Measure dropdown isn't rendered) so they aren't duplicated. */}
            {isMPRActive && (
            <>
            <ToolbarMoreMenuRow
              label={t("viewer.tools.delete-selected-measurement")}
              shortcut="SHIFT + D, DELETE"
              onClick={
                isMeasurementDeletionDisabled
                  ? undefined
                  : () => {
                      removeSelectedMeasurement();
                      setShowDropDown(false);
                    }
              }
              onMouseOver={() => setHoverDeleteSelected(true)}
              onMouseLeave={() => setHoverDeleteSelected(false)}
              disabled={isMeasurementDeletionDisabled}
            >
              <div className="highlight-tool">
                <DeleteIcon
                  color={
                    hoverDeleteSelected
                      ? theme.palette.activeTool
                      : theme.palette.inactiveTool
                  }
                />
              </div>
            </ToolbarMoreMenuRow>
            <ToolbarMoreMenuRow
              label={t("viewer.tools.delete-all-measurements")}
              shortcut="CTRL/CMD + DELETE"
              onClick={
                isMeasurementDeletionDisabled
                  ? undefined
                  : () => {
                      removeAllMeasurements();
                      setShowDropDown(false);
                    }
              }
              onMouseOver={() => setHoverDeleteAll(true)}
              onMouseLeave={() => setHoverDeleteAll(false)}
              disabled={isMeasurementDeletionDisabled}
            >
              <div className="highlight-tool">
                <DeleteIcon
                  color={
                    hoverDeleteAll
                      ? theme.palette.activeTool
                      : theme.palette.inactiveTool
                  }
                />
              </div>
            </ToolbarMoreMenuRow>
            </>
            )}
          </div>

          {/* Flyout panel — separate block to the right, only in portrait mode */}
          {flyoutContent && (
            <div
              className="viewer-tool-more-dropdown"
              style={flyoutPanelStyle}
              onMouseEnter={cancelHideFlyout}
              onMouseLeave={hideFlyout}
            >
              {flyoutContent}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ToolDropDown;
